import {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from "react";
import { computeOrderSummary } from "../constants/pricing";
import {
  fetchSubscriptionStatus,
  normalizeSubscriptionPayload,
} from "../api/subscription";
import { getSessionAuthToken } from "../api/auth";

const DEMO_SUBSCRIPTION = {
  planType: "monthly",
  currentSeats: 2,
  maxSeats: 2,
  isTrial: true,
  trialEndDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
  subscriptionEndDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
  paymentFailed: false,
};

// const Yearly_SUBSCRIPTION = {
//   planType: "annualWithEhr",
//   currentSeats: 2,
//   maxSeats: 2,
//   isTrial: false,
//   trialEndDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
//   subscriptionEndDate: new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString(),
//   paymentFailed: false,
// };


const SubscriptionContext = createContext(null);

const MS_PER_DAY = 1000 * 60 * 60 * 24;

function computeDaysLeft(dateString) {
  if (!dateString) return Infinity;
  const diff = new Date(dateString).getTime() - Date.now();
  return Math.ceil(diff / MS_PER_DAY);
}

/**
 * Dev/QA: sessionStorage.setItem('billingDemoOverride', JSON.stringify({ paymentFailed: true }))
 * Force demo only: REACT_APP_SUBSCRIPTION_SOURCE=demo
 * Force API only (no demo fallback): REACT_APP_SUBSCRIPTION_SOURCE=api
 */
function getSessionDemoOverrides() {
  try {
    const raw = sessionStorage.getItem("billingDemoOverride");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function getSubscriptionSourceMode() {
  const mode = (process.env.REACT_APP_SUBSCRIPTION_SOURCE || "auto").toLowerCase();
  return mode === "demo" || mode === "api" || mode === "auto" ? mode : "auto";
}

export function SubscriptionProvider({ children, overrides = {} }) {
  const [localOverrides, setLocalOverrides] = useState({});
  const [apiSubscription, setApiSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [dataSource, setDataSource] = useState("demo");

  const refreshSubscription = useCallback(async () => {
    const mode = getSubscriptionSourceMode();
    if (mode === "demo") {
      setApiSubscription(null);
      setDataSource("demo");
      setLoadError(null);
      setIsLoading(false);
      return;
    }

    if (!getSessionAuthToken()) {
      setApiSubscription(null);
      setDataSource("demo");
      setLoadError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setLoadError(null);

    try {
      const response = await fetchSubscriptionStatus();
      const normalized = normalizeSubscriptionPayload(response);
      if (normalized) {
        setApiSubscription(normalized);
        setDataSource("api");
      } else if (mode === "api") {
        setLoadError(new Error("Subscription API returned an empty payload"));
        setApiSubscription(null);
        setDataSource("api");
      } else {
        setApiSubscription(null);
        setDataSource("demo");
      }
    } catch (error) {
      if (mode === "api") {
        setLoadError(error);
        setApiSubscription(null);
        setDataSource("api");
      } else {
        console.warn("Subscription API unavailable, using demo fallback:", error.message);
        setApiSubscription(null);
        setDataSource("demo");
        setLoadError(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSubscription();
  }, [refreshSubscription]);

  const subscriptionBase = useMemo(() => {
    const base = apiSubscription || DEMO_SUBSCRIPTION;
    const merged = {
      ...base,
      ...getSessionDemoOverrides(),
      ...overrides,
      ...localOverrides,
    };
    const trialDaysLeft = computeDaysLeft(merged.trialEndDate);
    const subscriptionDaysLeft = computeDaysLeft(merged.subscriptionEndDate);

    return {
      ...merged,
      trialDaysLeft,
      subscriptionDaysLeft,
      canAddSeat: () => merged.currentSeats < merged.maxSeats,
      isTrialExpired: () => trialDaysLeft <= 0,
      isSubscriptionExpired: () => subscriptionDaysLeft <= 0,
      daysUntilExpiry: () => subscriptionDaysLeft,
    };
  }, [apiSubscription, overrides, localOverrides]);

  const [orderDetails, setOrderDetailsRaw] = useState(() => {
    try {
      const stored = sessionStorage.getItem("seismic_order_details");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const setOrderDetails = useCallback(({ planType, seatCount }) => {
    const summary = computeOrderSummary({ planType, seatCount });
    const newOrder = { planType, seatCount, ...summary };
    setOrderDetailsRaw(newOrder);
    try {
      sessionStorage.setItem("seismic_order_details", JSON.stringify(newOrder));
    } catch {
      /* ignore */
    }
  }, []);

  const clearOrderDetails = useCallback(() => {
    setOrderDetailsRaw(null);
    try {
      sessionStorage.removeItem("seismic_order_details");
    } catch {
      /* ignore */
    }
  }, []);

  const updateSubscription = useCallback((updates) => {
    setLocalOverrides((prev) => ({ ...prev, ...updates }));
  }, []);

  const value = useMemo(
    () => ({
      ...subscriptionBase,
      orderDetails,
      setOrderDetails,
      clearOrderDetails,
      updateSubscription,
      refreshSubscription,
      isLoading,
      loadError,
      dataSource,
    }),
    [
      subscriptionBase,
      orderDetails,
      setOrderDetails,
      clearOrderDetails,
      updateSubscription,
      refreshSubscription,
      isLoading,
      loadError,
      dataSource,
    ]
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error("useSubscription must be used within a <SubscriptionProvider>");
  }
  return context;
}

export default SubscriptionContext;
